/**
 * @vuelto-surface
 * mobile: ok
 * desktop: ok
 * strategy: unified
 */
import React, { useMemo, useState } from 'react';
import { StyleSheet, Text, TouchableOpacity, useWindowDimensions, View } from 'react-native';
import { Ionicons } from '@expo/vector-icons';
import { ModalNestedPicker } from '../../shared/modals/ModalNestedPicker';
import { FiscalBrandFillSegmented } from './FiscalBrandFillSegmented';
import {
  monotributoCategoryPickerOptions,
  monotributoCategorySelectLabel,
} from '../domain/fiscalPresentation';
import type { TaxRegime } from '../domain/fiscalTypes';
import { spacing } from '../../shared/theme/spacing'
import { typography } from '../../shared/theme/typography';
import { ticketColors } from '../../shared/theme/ticketColors';

const REGIME_OPTIONS = [
  {
    value: 'monotributo' as const,
    label: 'Monotributo',
    shortLabel: 'Monotributo',
    subtitle: 'Emitís factura C',
  },
  {
    value: 'responsable_inscripto' as const,
    label: 'Responsable inscripto',
    shortLabel: 'Responsable inscripto',
    subtitle: 'Emitís factura A o B',
  },
];

export type TaxRegimeSelectorProps = {
  value: TaxRegime;
  monotributoCategory: string;
  onChangeRegime: (regime: TaxRegime) => void;
  onChangeCategory: (category: string) => void;
  disabled?: boolean;
};

export function TaxRegimeSelector({
  value,
  monotributoCategory,
  onChangeRegime,
  onChangeCategory,
  disabled = false,
}: TaxRegimeSelectorProps) {
  const { width } = useWindowDimensions();
  const isWide = width >= 768;
  const [categoryPickerOpen, setCategoryPickerOpen] = useState(false);
  const categoryOptions = useMemo(() => monotributoCategoryPickerOptions(), []);
  const categoryLabel = monotributoCategorySelectLabel(monotributoCategory);

  return (
    <View style={styles.wrap}>
      <Text style={styles.fieldLabel}>Régimen</Text>
      <FiscalBrandFillSegmented
        options={REGIME_OPTIONS}
        value={value}
        onChange={onChangeRegime}
        disabled={disabled}
        vertical={!isWide}
        accessibilityLabel="Régimen impositivo"
      />
      {value === 'monotributo' ? (
        <View style={styles.categoryBlock}>
          <Text style={styles.fieldLabel}>Categoría</Text>
          <TouchableOpacity
            style={[styles.selectTrigger, disabled && styles.disabled]}
            onPress={() => {
              if (!disabled) setCategoryPickerOpen(true);
            }}
            disabled={disabled}
            accessibilityRole="button"
            accessibilityLabel={`Categoría monotributo: ${categoryLabel}. Abrir lista`}
            accessibilityState={{ disabled }}
          >
            <Text style={styles.selectValue} numberOfLines={1}>
              {categoryLabel}
            </Text>
            <Ionicons name="chevron-down" size={18} color={ticketColors.ink2} />
          </TouchableOpacity>
        </View>
      ) : (
        <Text style={styles.detailBody}>
          Factura A si el cliente también es responsable inscripto. Factura B para el resto.
        </Text>
      )}

      <ModalNestedPicker
        visible={categoryPickerOpen}
        title="Categoría monotributo"
        options={categoryOptions}
        selectedValue={monotributoCategory}
        onSelect={(next) => {
          onChangeCategory(next);
          setCategoryPickerOpen(false);
        }}
        onClose={() => setCategoryPickerOpen(false)}
      />
    </View>
  );
}

const styles = StyleSheet.create({
  wrap: { gap: spacing.sm },
  categoryBlock: { gap: spacing.sm },
  fieldLabel: {
    ...typography.caption,
    color: ticketColors.ink2,
    fontWeight: '700',
  },
  detailBody: { ...typography.caption, color: ticketColors.ink2, lineHeight: 18 },
  selectTrigger: {
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'space-between',
    gap: spacing.sm,
    minHeight: 44,
    borderWidth: 1,
    borderColor: ticketColors.ruleSoft,
    borderRadius: 10,
    backgroundColor: ticketColors.paper,
    paddingHorizontal: spacing.md,
    paddingVertical: spacing.sm,
  },
  selectValue: {
    ...typography.body,
    flex: 1,
    minWidth: 0,
    color: ticketColors.ink,
    fontWeight: '600',
  },
  disabled: { opacity: 0.55 },
});
